import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useBusinessConfig } from "~contexts/BusinessConfigContext/BusinessConfigContext";
import { useCurrentEmployee } from "~contexts/CurrentEmployeeContext/CurrentEmployeeContext.tsx";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext";
import type { Employee } from "~generated";
import { ManagersService } from "~generated";
import type { Resource } from "~lib/resourceHelpers.ts";
import { getResourceName, ResourceType } from "~lib/resourceHelpers.ts";
import { useAllEmployees } from "./employees";
import { findEntity, type QueryOpts } from "./helpers.ts";
import { managerKeys } from "./managerKeys.ts";

async function getManagers({ tenantId }: { tenantId: string }) {
  const employees = await ManagersService.getTenantManagers(tenantId);
  if (!employees?.employees) {
    return [];
  }
  return employees.employees.sort((a, b) =>
    getResourceName(a).localeCompare(getResourceName(b)),
  );
}

interface UseManagersQueryOptions {
  useQueryOptions?: QueryOpts<Employee[], ReturnType<typeof managerKeys.list>>;
}
export function useManagers({ useQueryOptions }: UseManagersQueryOptions = {}) {
  const currentTenant = useCurrentTenant();

  const queryResult = useQuery({
    ...useQueryOptions,
    queryKey: managerKeys.list(currentTenant.id),
    queryFn: async () => getManagers({ tenantId: currentTenant.id }),
  });

  const filteredData = useMemo(
    () => queryResult.data?.filter((employee) => !employee.resigned_at),
    [queryResult.data],
  );

  return {
    ...queryResult,
    data: filteredData,
  };
}

export function useSuperiorConstructionSiteManagerOfCurrentEmployee() {
  const { currentEmployee } = useCurrentEmployee();

  const { isLoading, getSuperiorConstructionSiteManagerOf } =
    useSuperiorConstructionSiteManager();

  return {
    constructionSiteManagerOfCurrentEmployee:
      getSuperiorConstructionSiteManagerOf(currentEmployee),
    isLoading,
  };
}

export function useSuperiorConstructionSiteManager() {
  const { data: allEmployees, isLoading: isLoadingAllEmployees } =
    useAllEmployees();

  const {
    jobTitleRoleMapping: { managingDirector: managingDirectorTitles },
  } = useBusinessConfig();

  const getSuperiorConstructionSiteManagerOf = (employee: Employee) => {
    const superior = findSuperiorConstructionSiteManager(
      employee,
      allEmployees || [],
    );
    if (superior) {
      return superior;
    }

    return allEmployees?.find(
      (e) =>
        managingDirectorTitles.includes(e.job_title) &&
        e.tenant_id === employee.tenant_id,
    );
  };

  const getSuperiorConstructionSiteManagerOfResource = (resource: Resource) => {
    if (resource.type !== ResourceType.EMPLOYEE) {
      return undefined;
    }

    const employee = findEntity(allEmployees, resource.id);

    return employee && getSuperiorConstructionSiteManagerOf(employee);
  };

  return {
    getSuperiorConstructionSiteManagerOf,
    getSuperiorConstructionSiteManagerOfResource,
    isLoading: isLoadingAllEmployees,
  };
}

function findSuperiorConstructionSiteManager(
  currentEmployee: Employee,
  allEmployees: Employee[],
) {
  let superior: Employee | undefined = currentEmployee;
  let depth = 0;

  const isSuperior = (e: Employee) => e.id === superior?.superior_id;

  while (superior && depth < 10) {
    if (superior?.is_construction_site_manager) {
      return superior;
    }
    superior = allEmployees?.find(isSuperior);
    depth += 1;
  }

  return undefined;
}
